<template>
  <div :class="{ 'ru-locale': $i18n.locale === 'ru' }" class="app-wrapper">
    <NuxtLayout>
      <NuxtPage />
      <el-dialog
        v-model="showPopupValue"
        :append-to-body="true"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        :title="$t('notificationError')"
        @closed="close"
      >
        <div class="error-wrapper">
          {{ error }}
        </div>
        <div class="btns">
          <button class="btn-primary" @click="close">{{ $t('coreClose') }}</button>
        </div>
      </el-dialog>
    </NuxtLayout>
    <RegisterUserModal />
    <GeneralSoundControlsModal />
    <TransactionNotifications :key="notificationUpdateVersion" />
    <UIUserBlockInfoModal />
    <ChristmasPromoModal v-model="promoModalOpen" :promo-mode="promoMode" />
    <V2MigrationModal
      v-if="v2MigrationModalOpen"
      v-model="v2MigrationModalOpen"
      :shutdown-date="v2ShutdownDate"
      :v2-game-url="v2GameUrl"
    />
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#app';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { Multicall__factory, Season__factory, SeasonRewardsYear1__factory } from 'public/abi/evm/types';
import usePromoModal from '~/composables/usePromoModal';
import useProvider from '~/composables/useProvider';
import useV2Migration from '~/composables/useV2Migration';
import type { IUserModel, UserReferralStatus } from '~/types/apiService';
import { nonNullable } from '~/utils/non-nullable';
import ChristmasPromoModal from '~/widgets/christmas/ChristmasPromoModal.vue';
import V2MigrationModal from '~/widgets/v2-migration/V2MigrationModal.vue';
import type { TSeasonConfig } from './types/season';
import { chunkArray } from './utils';
import { apiUrls } from './utils/constants';

const store = useMainStore();
const { notificationUpdateVersion } = toRefs(store);
const { address } = useWeb3ModalAccount();
const { apiUrl, blockchain } = useEnvs();
const { t } = useI18n();
const refId = useCookie('refId');
const { getProvider } = useProvider();
const config = useRuntimeConfig();

const year2Indexes = config.public.env === 'prod' ? 41 : 1006;
const selectYearContract = (index: number) => {
  if (index >= year2Indexes) return blockchain.contracts.SEASON_REWARDS_YEAR_2;
  return blockchain.contracts.SEASON_REWARDS_YEAR_1;
};

const userCreated = ref(false);
const claimBtnTokenIcon = ref('');

const seasonContract = Season__factory.connect(blockchain.contracts.season, getProvider());

const seasonContractInterface = Season__factory.createInterface();
const seasonYearContractInterface = SeasonRewardsYear1__factory.createInterface();

const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
const initialSeasonId = await seasonContract.currentSeason();
const initialSeasonsCount = await seasonContract.getSeasonsLength();

const currentSeasonId = ref<number>(BigNumber(initialSeasonId.toString()).toNumber());
const seasonsCount = ref<number>(BigNumber(initialSeasonsCount.toString()).toNumber());

const { data, execute } = useFetch<TSeasonConfig>(() => apiUrls.seasons.config(currentSeasonId.value), {
  baseURL: apiUrl,
  immediate: false,
  key: 'seasonsConfig',
  watch: [address, currentSeasonId, () => store.notificationUpdateVersion]
});

const getNextSundayNoonUTC = (now: Date): Date => {
  const utcDay = now.getUTCDay(); // 0 = Sunday
  const utcHour = now.getUTCHours();

  // If it's Sunday and before 12:00 UTC, the season ends today at noon
  const isSundayBeforeNoon = utcDay === 0 && utcHour < 12;
  const daysUntilSunday = isSundayBeforeNoon ? 0 : (7 - utcDay) % 7 || 7;

  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysUntilSunday, 12, 0, 0, 0)
  );

  return end;
};

const seasonEndDate = computed(() => {
  const now = new Date();

  // Weekly season mode: statically compute next Sunday 12:00 UTC
  if (data.value?.endDate === data.value?.startDate) {
    return getNextSundayNoonUTC(now);
  }

  // Normal mode: use backend-defined end date
  return new Date(data.value?.endDate || Date.now());
});

const { timeLeft } = useCountdownDate(seasonEndDate, 'second');

const calldata = computed(() =>
  Array(BigNumber(currentSeasonId.value).toNumber())
    .fill(notificationUpdateVersion.value)
    .map((item, index) => {
      return {
        getUserPositionInSeason: [
          blockchain.contracts.season,
          address.value
            ? seasonContractInterface.encodeFunctionData('getUserPositionInSeason', [index, address.value, 1000])
            : null
        ] as const,
        getSeasonRewardsPositionsCount: [
          selectYearContract(index),
          seasonYearContractInterface.encodeFunctionData('getSeasonRewardsPositionsCount', [index])
        ] as const
      };
    })
);

const positionInSeasonMulticallData = asyncComputed(async () => {
  const calls = nonNullable(
    calldata.value.map((item, index) => {
      if (
        BigNumber(notificationUpdateVersion.value).isNaN() ||
        BigNumber(timeLeft.minutes).isNaN() ||
        BigNumber(index).isNaN() ||
        !address.value
      )
        return null;
      return item.getUserPositionInSeason;
    })
  );

  const chunks = chunkArray<(typeof calls)[0]>(calls, 40);

  let allResults: any[] = [];

  for (const chunk of chunks) {
    const res = await multicallContract?.tryAggregate.staticCall(
      true,
      chunk.map((item) => ({
        target: item[0],
        callData: item[1] || '0x'
      }))
    );
    const decoded = res.map((item: any) =>
      seasonContractInterface.decodeFunctionResult('getUserPositionInSeason', item[1])
    );
    allResults = allResults.concat(decoded);
  }

  return allResults;
});

const seasonPrizePositionsCountMulticallData = asyncComputed(async () => {
  const calls = nonNullable(
    calldata.value.map((item, index) => {
      if (
        BigNumber(notificationUpdateVersion.value).isNaN() ||
        BigNumber(timeLeft.minutes).isNaN() ||
        BigNumber(index).isNaN()
      )
        return null;

      return item.getSeasonRewardsPositionsCount;
    })
  );

  const chunks = chunkArray(calls, 40);

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const res = await multicallContract?.tryAggregate.staticCall(
        true,
        chunk.map((item) => ({
          target: item[0],
          callData: item[1] || '0x'
        }))
      );
      return res.map((item: any) =>
        seasonYearContractInterface.decodeFunctionResult('getSeasonRewardsPositionsCount', item[1])
      );
    })
  );

  return results.flat();
});

const isClaimedMulticallData = asyncComputed(async () => {
  const calls = nonNullable(
    calldata.value.map((_, i) => {
      if (
        BigNumber(notificationUpdateVersion.value).isNaN() ||
        BigNumber(timeLeft.minutes).isNaN() ||
        BigNumber(i).isNaN() ||
        BigNumber(positionInSeasonMulticallData.value?.[i][0]).isNaN()
      )
        return null;

      return [
        selectYearContract(i),
        seasonYearContractInterface.encodeFunctionData('isSeasonPositionClaimed', [
          i,
          positionInSeasonMulticallData.value?.[i][0] || 100
        ])
      ];
    })
  );

  const chunks = chunkArray(calls, 40);

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const res = await multicallContract?.tryAggregate.staticCall(
        true,
        chunk.map((item) => ({
          target: item[0],
          callData: item[1] || '0x'
        }))
      );
      return res.map((item: any) =>
        seasonYearContractInterface.decodeFunctionResult('isSeasonPositionClaimed', item[1])
      );
    })
  );

  return results.flat();
});

const getSeasonPositionConfigMulticallData = asyncComputed(async () => {
  const calls = nonNullable(
    calldata.value.map((_, i) => {
      if (
        BigNumber(notificationUpdateVersion.value).isNaN() ||
        BigNumber(timeLeft.minutes).isNaN() ||
        BigNumber(i).isNaN() ||
        BigNumber(positionInSeasonMulticallData.value?.[i][0]).isNaN()
      )
        return null;

      return [
        selectYearContract(i),
        seasonYearContractInterface.encodeFunctionData('getSeasonPositionRewards', [
          i,
          positionInSeasonMulticallData.value?.[i][0] || 100
        ])
      ];
    })
  );

  const chunks = chunkArray(calls, 40);

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const res = await multicallContract?.tryAggregate.staticCall(
        true,
        chunk.map((item) => ({
          target: item[0],
          callData: item[1] || '0x'
        }))
      );
      return res.map((item: any) =>
        seasonYearContractInterface.decodeFunctionResult('getSeasonPositionRewards', item[1])
      );
    })
  );

  return results.flat();
});

watch(
  currentSeasonId,
  async () => {
    if (import.meta.server) return;
    await injectSeasonBgStyle(currentSeasonId.value);
  },
  { immediate: true }
);

const isUnclaimedPositions = computed(() => {
  return new Array(BigNumber(currentSeasonId.value).toNumber()).fill(notificationUpdateVersion.value).map((id, i) => {
    const positionInSeason = positionInSeasonMulticallData.value?.[i]?.[0];
    const seasonPrizePositionsCount = seasonPrizePositionsCountMulticallData.value?.[i]?.[0];
    const seasonPositionConfig = getSeasonPositionConfigMulticallData.value?.[i]?.[0];

    //if (positionInSeason > 0n && seasonPrizePositionsCount > 0n)
    //  console.log(
    //    'xxf position log',
    //    'season=',
    //    i,
    //    'position=',
    //    positionInSeason,
    //    'prize=',
    //    seasonPrizePositionsCount,
    //    'claimed=',
    //    isClaimedMulticallData.value?.[i][0]
    //  );

    return BigNumber(positionInSeason).isNaN() ||
      BigNumber(positionInSeason).isZero() ||
      BigNumber(positionInSeason).isGreaterThan(seasonPrizePositionsCount) ||
      !seasonPositionConfig
      ? null
      : isClaimedMulticallData.value?.[i][0];
  });
});

const isShowClaimSeasonRewards = computed(() => {
  return (
    isUnclaimedPositions.value.some((config) => typeof config === 'boolean' && !config && isUnclaimedPositions.value) &&
    positionInSeasonMulticallData.value
  );
});

watch(
  isUnclaimedPositions,
  (newIsUnclaimedPositions) => {
    if (!newIsUnclaimedPositions) return;

    if (isShowClaimSeasonRewards.value) {
      claimBtnTokenIcon.value = 'season_chest';
    } else {
      claimBtnTokenIcon.value = '';
      return;
    }
  },
  { immediate: true }
);

watch(
  currentSeasonId,
  () => {
    if (BigNumber(currentSeasonId.value).isNaN()) return;
    execute();
  },
  { immediate: true }
);

watch(
  () => timeLeft.minutes,
  async () => {
    await sleep(2000);
    currentSeasonId.value = parseInt((await seasonContract.currentSeason()).toString());
    seasonsCount.value = parseInt((await seasonContract.getSeasonsLength()).toString());
  },
  { immediate: true }
);

watch(
  notificationUpdateVersion,
  async () => {
    if (BigNumber(seasonsCount.value).lte(currentSeasonId?.value)) return;
    seasonsCount.value = parseInt((await seasonContract.getSeasonsLength()).toString());
  },
  { immediate: true }
);

const position = asyncComputed<number>(async () => {
  if (BigNumber(notificationUpdateVersion.value).isNaN() || !address.value) return 0;

  return BigNumber(currentSeasonId.value).isGreaterThanOrEqualTo(seasonsCount.value)
    ? 0
    : parseInt((await seasonContract.getUserPositionInSeason(currentSeasonId.value, address.value, 100)).toString());
});

const seasonsDaysLeftText = computed(() => {
  if (timeLeft.ended) {
    return t('seasonTimeEnded');
  }

  if (timeLeft.days === 1) {
    return t('coreDay');
  }

  return t('coreDays');
});

const error = computed(() => store.error);
const showPopupValue = computed(() => !!store.error);

const close = () => {
  store.setError('');
};

const { data: userData } = useNuxtData<IUserModel>('userData');

const { execute: createUser } = useFetch<IUserModel>(apiUrls.v1.user, {
  method: 'POST',
  baseURL: apiUrl,
  body: {
    address: address,
    pendingReferrerId: refId
  },
  immediate: false,

  onResponse: async ({ response }) => {
    if (!response.ok) {
      return;
    }
    await refreshNuxtData('userData');
  }
});

watchEffect(() => {
  userCreated.value = !!userData.value;

  const hasAddress = address.value;
  const hasRef = refId.value;
  const noUser = !userData.value;

  if (hasAddress && hasRef && noUser && !userCreated.value) {
    userCreated.value = true;
    createUser();
  }
});

const { data: referralStatus } = useFetch<UserReferralStatus>(`${apiUrls.v1.user}/referral-status`, {
  baseURL: apiUrl,
  watch: [address],
  query: {
    address
  },
  key: 'referralStatus',
  immediate: false
});

watch(referralStatus, (status) => {
  if (!status?.isReferralMember) {
    return;
  }

  store.setIsJoiningToReferral(false);
});

provide('currentSeasonId', currentSeasonId);
provide('seasonsCount', seasonsCount);
provide('playerPosition', position);
provide('seasonEndDate', seasonEndDate);
provide('timeLeft', timeLeft);
provide('seasonsDaysLeftText', seasonsDaysLeftText);
provide('isUnclaimedPositions', isShowClaimSeasonRewards);
provide('claimBtnTokenIcon', claimBtnTokenIcon);
provide('ended', timeLeft.ended);

// Christmas/New Year promo modal
const { shouldShow: shouldShowPromo, mode: promoMode, markAsShown: markPromoAsShown } = usePromoModal();
const promoModalOpen = ref(false);

// Watch for promo modal state changes
watch(shouldShowPromo, (newValue) => {
  if (newValue && import.meta.client) {
    promoModalOpen.value = true;
  }
});

// Mark as shown when modal is closed
watch(promoModalOpen, (isOpen) => {
  if (!isOpen && import.meta.client) {
    markPromoAsShown();
  }
});

// v2 migration promo
const {
  shouldShow: shouldShowV2Migration,
  shutdownDate: v2ShutdownDate,
  v2GameUrl,
  markAsShown: markV2MigrationAsShown
} = useV2Migration();
const v2MigrationModalOpen = ref(false);

watch(shouldShowV2Migration, (value) => {
  if (value && import.meta.client) {
    v2MigrationModalOpen.value = true;
  }
});

watch(v2MigrationModalOpen, (isOpen) => {
  if (!isOpen && import.meta.client) {
    markV2MigrationAsShown();
  }
});

onMounted(() => {
  updateProcessingTransactions();
  store.updateVersion();
});
</script>

<style lang="scss" scoped>
.app-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100% - 1px);
}

.error-wrapper {
  margin-bottom: 12px;
  text-align: center;
}
</style>

<!-- TODO: move this global styles to the main scss file -->
<style lang="scss">
.el-dialog.login-popup {
  width: 280px;
}

.modalCls {
  text-align: center;
}

.el-dialog.video-popup {
  width: 100%;
  max-width: 720px;

  .el-dialog__body {
    max-width: 700px;
  }
}

.el-dialog.full-body {
  width: 100%;
  max-width: 720px;

  .el-dialog__body {
    max-width: 100%;
  }
}

.el-dialog.buy-popup {
  max-width: 550px;
  min-width: 450px;

  .show-close {
    padding-right: 0;
  }

  @media screen and (max-width: 479px) {
    overflow-x: hidden;
    min-width: unset;
  }
}

.el-dialog.info {
  max-width: 800px;
  padding: 25px;

  .el-dialog__body {
    max-width: unset;
  }
}

.el-dialog.crafting {
  max-width: 1024px;
  width: 100%;

  .el-dialog__body {
    max-width: 100%;
  }

  @media screen and (max-width: 1400px) {
    max-width: 710px;
  }

  @media screen and (max-width: 900px) {
    overflow: auto;
  }
}

.el-dialog.is-fullscreen {
  width: 100%;
  max-width: 100%;
}

.el-dialog.without-header .el-dialog__header {
  height: 0;

  @media screen and (max-width: 900px) {
    height: auto;
  }
}

.el-dialog {
  border-radius: 12px;
  // border: 2px solid var(--main-blue);
  backdrop-filter: blur(5px);
  background-color: rgba(2, 27, 38, 0.8);
  overflow: hidden;
  max-width: 574px;
  box-shadow: 0 0 11px 5px rgba(0, 0, 0, 0.4);

  &__header {
    text-align: center;
    margin: 0;
    padding: 6px 0 0 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    font-family: Grenze, sans-serif;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.273em;

    @media screen and (max-width: 991px) {
      padding: 4px 0px 0px 30px;
    }

    span {
      max-width: 100%;
    }
  }

  .el-dialog__title {
    font-size: 32px;
    color: #fff;

    @media screen and (max-width: 991px) {
      padding-right: 0px;
      font-size: 30px;
    }
  }

  &__body {
    max-width: 415px;
    margin: auto;
    padding-bottom: 30px;
    padding-top: 30px;
    line-height: 1.5;
  }

  &__close,
  .el-icon svg {
    height: 30px;
    width: 30px;
  }
}

.popup-canceled {
  border: 2px solid var(--canceled-color);
}

.popup-confirm {
  border: 2px solid var(--confirm-color);
}

.el-button {
  display: flex;
  padding: 0 38px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 86px;
  background: var(--main-blue);
  text-align: center;
  font-size: 24px;
  color: var(--main-text-color);
  border: 1px solid transparent;
  height: 47px;

  &.is-fullscreen {
    height: 100dvh;
  }

  @media (hover: hover) {
    &:active,
    &:focus,
    &:hover {
      background: transparent;
      border: 1px solid #939393;
    }
  }
}

.table .el-table {
  --el-table-border-color: none;
  font-size: 24px;
  color: rgba(255, 255, 255, 0.55);

  &:not(.line-rounded-icon, .line-square-icon) {
    font-family: Grenze, sans-serif;
  }

  @media screen and (max-width: 991px) {
    font-size: 14px;
  }
}

.table .el-table tr {
  background-color: transparent;
}

.table .el-table tr:nth-child(2n) {
  background-color: rgba(0, 0, 0, 0.3);
}

.table .el-table th.el-table__cell {
  background: var(--dark-2);
  background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.5));
  margin-bottom: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  background-color: transparent;
  color: #fff;
  font-family: Grenze, sans-serif;
  font-size: 24px;
  font-weight: 400;

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }

  @media screen and (max-width: 767px) {
    font-size: 14px;
  }

  @media screen and (max-width: 479px) {
    font-size: 12px;
  }
}

.el-table .el-table__cell {
  @media screen and (max-width: 991px) {
    font-size: 16px;
  }

  @media screen and (max-width: 767px) {
    font-size: 14px;
  }

  @media screen and (max-width: 479px) {
    font-size: 12px;
  }
}

.el-table .cell {
  word-break: normal;
}

.el-table th.el-table__cell:first-child {
  border-radius: 16px 0 0 0;
}

.el-table th.el-table__cell:last-child {
  border-radius: 0 16px 0 0;
}

.el-icon.el-dialog__close svg {
  display: none;
}

.el-dialog__headerbtn {
  height: 20px;
  width: 20px;
  top: 23px;
  right: 20px;
  z-index: 999;
}

.el-icon.el-dialog__close {
  background-image: url('~/public/img/icons/bone_close.svg');
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50%;

  @media (hover: hover) {
    &:hover {
      opacity: 0.6;
    }
  }
}

.btns {
  display: flex;
  justify-content: center;
}

.el-button + .el-button {
  margin-left: 0 !important;
}

.el-checkbox__label {
  color: var(--second-text-color);
  text-align: left;
  font-family: Grenze, sans-serif;
  white-space: initial;
  line-height: 1.333em;

  @media screen and (max-width: 767px) {
    font-size: 14px !important;
  }
}

.el-checkbox {
  display: flex;
  align-items: flex-start;
  height: auto !important;
}

.el-checkbox__input {
  margin-top: 4px;
}

.el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--main-text-color);
}

.scene-wrapper {
  width: 100%;
  height: calc(100dvh - 80px);
}

.video-bg > video {
  width: 100%;
  height: auto;
}
</style>
